/** MailboxContext — Personal Profile ↔ Production House Page toggle.
 *
 * Every signed-in user maintains one Personal Profile plus zero-or-more
 * PH Pages (any org they own, sit on the committee of, or are producers
 * for). The active context controls:
 *   - Which inbox stream the ChatDrawer shows (`?context=`)
 *   - Which "You are posting as" identity appears at the top of the DM
 *     composer
 *
 * Contract:
 *   activeContext = { id: "self" | "org:<uuid>", kind, title, org_id? }
 *   contexts      = [ ...same shape, always self first ]
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "callsheet_active_context";

const MailboxCtx = createContext({
  contexts: [{ id: "self", kind: "personal", title: "You", icon: "user" }],
  activeContext: { id: "self", kind: "personal", title: "You", icon: "user" },
  setActive: () => {},
  refresh: () => {},
});

const SELF_CONTEXT = { id: "self", kind: "personal", title: "You", icon: "user" };

export function MailboxProvider({ children }) {
  const { user } = useAuth();
  const [contexts, setContexts] = useState([SELF_CONTEXT]);
  const [activeContext, setActiveContext] = useState(SELF_CONTEXT);

  const refresh = useCallback(async () => {
    if (!user) {
      setContexts([SELF_CONTEXT]);
      setActiveContext(SELF_CONTEXT);
      return;
    }
    try {
      const { data } = await api.get("/me/mailbox/contexts");
      const items = data.items || [SELF_CONTEXT];
      setContexts(items);
      // Restore the last-picked context from storage, if still valid.
      const savedId = localStorage.getItem(STORAGE_KEY);
      const restored = savedId ? items.find((c) => c.id === savedId) : null;
      setActiveContext(restored || items[0] || SELF_CONTEXT);
    } catch (err) {
      console.warn("[mailbox] contexts fetch failed:", err?.message);
      setContexts([SELF_CONTEXT]);
      setActiveContext(SELF_CONTEXT);
    }
  }, [user?.id]);

  useEffect(() => { void refresh(); }, [refresh]);

  const setActive = useCallback((next) => {
    if (!next?.id) return;
    setActiveContext(next);
    try { localStorage.setItem(STORAGE_KEY, next.id); }
    catch { /* storage full — silent */ }
  }, []);

  return (
    <MailboxCtx.Provider value={{ contexts, activeContext, setActive, refresh }}>
      {children}
    </MailboxCtx.Provider>
  );
}

export const useMailbox = () => useContext(MailboxCtx);
